import { DropdownMenuLinkItem } from '@awpmarine/ui/components/dropdown-menu-link-item';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@awpmarine/ui/components/ui/dropdown-menu';
import { useRouterState } from '@tanstack/react-router';
import { cn } from 'cn';
import { CheckIcon, GlobeIcon } from 'lucide-react';

import { hreflangTags, languageTags, nativeNames, shortNames } from '@/lib/locale';
import { m } from '@/paraglide/messages';
import { type Locale, getLocale, locales, localizeHref } from '@/paraglide/runtime';

/**
 * The current page in every locale. Plain links rather than router navigation, so the page loads
 * fresh in the new language, works before hydration, and is a link a crawler can follow.
 */
export function useLanguageLinks() {
  const current = getLocale();
  const path = useRouterState({
    select: ({ location }) => `${location.pathname}${location.searchStr}${location.hash ? `#${location.hash}` : ''}`,
  });

  return locales.map((locale: Locale) => ({
    locale,
    name: nativeNames[locale],
    href: localizeHref(path, { locale }),
    current: locale === current,
    // On the link, so a screen reader reads each name in its own language.
    props: { lang: languageTags[locale], hrefLang: hreflangTags[locale] },
  }));
}

interface LanguageMenuProps {
  /** Show the current language as a short code, where the header has little room. */
  short?: boolean;
  /** Where the menu opens; the footer's opens upwards. */
  side?: 'top' | 'bottom';
  className?: string;
}

/** The current language and a menu of the others, as links to this page in each. */
export function LanguageMenu({ short = false, side = 'bottom', className }: LanguageMenuProps) {
  const links = useLanguageLinks();
  const current = getLocale();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label={m.language_label()}
        className={cn(
          'flex min-h-11 items-center gap-1.5 rounded-full border border-[#cfd7e2] px-3.5 font-semibold transition-colors hover:border-brand hover:text-brand data-popup-open:border-brand data-popup-open:text-brand',
          className,
        )}
      >
        <GlobeIcon className='size-4.5' />
        {short ? shortNames[current] : nativeNames[current]}
      </DropdownMenuTrigger>
      <DropdownMenuContent align='end' side={side} sideOffset={8} className='w-auto min-w-44 rounded-xl p-1.5'>
        {links.map((link) => (
          <DropdownMenuItem
            key={link.locale}
            render={
              <DropdownMenuLinkItem href={link.href} aria-current={link.current ? 'page' : undefined} {...link.props} />
            }
            className='justify-between gap-4 rounded-lg px-3 py-2.5 text-base'
          >
            {link.name}
            {link.current && <CheckIcon className='size-4.5 text-brand' />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
